import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Wallet,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Search,
  Filter,
  X,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../lib/utils/errorMessage";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import {
  TableRoot,
  Table,
  THead,
  TR,
  TH,
  TBody,
  TD,
} from "../../components/UI/Table";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { Account, Transaction } from "../../lib/sdk/index";
import { useAuthStore } from "../../stores/authStore";
import { useFeatureAccessStore } from "../../stores/featureAccessStore";
import { formatDate, safeParseNumber } from "../../lib/utils/formatting";
import {
  useAccounts,
  useRecentTransactions,
  useTransactions,
  useCreateAccount,
  useUpdateAccount,
  useDeleteAccount,
  useCreateTransaction,
  useUpdateTransaction,
} from "../../hooks/useQueries";

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Accounts: React.FC = () => {
  const { user } = useAuthStore();
  // `manageAccounts` (or SuperAdmin) grants complete access. Users with only
  // `viewAccounts` get a read-only view of accounts plus the ability to add
  // and view transactions; everything else is hidden.
  const hasFeature = useFeatureAccessStore((s) => s.hasFeature);
  const canManage = hasFeature("manageAccounts");

  // Use cached React Query hooks
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 50,
    search: "",
    status: "",
  });
  const {
    data: accountsData,
    isLoading: accountsLoading,
    refetch: refetchAccounts,
  } = useAccounts(searchParams);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Transaction filters - MUST be declared before use
  const [txAccountFilter, setTxAccountFilter] = useState("");
  const [txTypeFilter, setTxTypeFilter] = useState("");
  const [txStartDate, setTxStartDate] = useState("");
  const [txEndDate, setTxEndDate] = useState("");
  const [txMinAmount, setTxMinAmount] = useState("");
  const [txMaxAmount, setTxMaxAmount] = useState("");

  // Transaction search params
  const [txSearchParams, setTxSearchParams] = useState({
    page: 1,
    limit: 50,
    accountId: "",
    type: "",
    startDate: "",
    endDate: "",
    minAmount: "",
    maxAmount: "",
  });

  // Use filtered transactions query when filters are active, otherwise use recent transactions
  const hasTxFilters = !!(
    txAccountFilter ||
    txTypeFilter ||
    txStartDate ||
    txEndDate ||
    txMinAmount ||
    txMaxAmount
  );
  const {
    data: filteredTransactionsData,
    isLoading: filteredTxLoading,
    refetch: refetchFilteredTx,
  } = useTransactions(hasTxFilters ? (txSearchParams as any) : {}, {
    enabled: hasTxFilters,
  });
  const {
    data: recentTransactionsData = [],
    isLoading: recentTxLoading,
    refetch: refetchRecentTx,
  } = useRecentTransactions(50, {
    enabled: !hasTxFilters,
  });

  const accounts =
    accountsData?.data || (Array.isArray(accountsData) ? accountsData : []);
  const transactions = hasTxFilters
    ? (filteredTransactionsData as any)?.data ||
      (Array.isArray(filteredTransactionsData) ? filteredTransactionsData : [])
    : Array.isArray(recentTransactionsData)
      ? recentTransactionsData
      : (recentTransactionsData as any)?.data || [];
  const loading = accountsLoading;
  const tableLoading =
    accountsLoading || (hasTxFilters ? filteredTxLoading : recentTxLoading);

  // Refetch function for transactions
  const refetchTransactions = hasTxFilters
    ? refetchFilteredTx
    : refetchRecentTx;

  // Modal states
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null);
  const [editingTransaction, setEditingTransaction] =
    useState<Transaction | null>(null);

  // Form data
  const [accountFormData, setAccountFormData] = useState({
    accountNumber: "",
    name: "",
    balance: "",
    autoUpdateFromDonations: false,
  });

  const [transactionFormData, setTransactionFormData] = useState({
    accountId: "",
    type: "credit" as "credit" | "debit",
    amount: "",
    description: "",
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = accountsData?.pagination?.totalPages || 1;
  const totalAccounts = accountsData?.pagination?.total || accounts.length;

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Update search params when filters change
  useEffect(() => {
    setSearchParams({
      page: currentPage,
      limit: 50,
      search: debouncedSearchTerm,
      status: statusFilter,
    });
  }, [currentPage, debouncedSearchTerm, statusFilter]);

  // Mutations
  const createAccountMutation = useCreateAccount({
    onSuccess: () => {
      toast.success("Account created successfully");
      setShowAccountForm(false);
      setEditingAccount(null);
      setAccountFormData({
        accountNumber: "",
        name: "",
        balance: "",
        autoUpdateFromDonations: false,
      });
      refetchAccounts();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, "Failed to save account"));
    },
  });

  const updateAccountMutation = useUpdateAccount({
    onSuccess: () => {
      toast.success("Account updated successfully");
      setShowAccountForm(false);
      setEditingAccount(null);
      setAccountFormData({
        accountNumber: "",
        name: "",
        balance: "",
        autoUpdateFromDonations: false,
      });
      refetchAccounts();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, "Failed to save account"));
    },
  });

  const deleteAccountMutation = useDeleteAccount({
    onSuccess: () => {
      toast.success("Account deleted successfully");
      setShowDeleteConfirm(false);
      setDeletingAccount(null);
      refetchAccounts();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, "Failed to delete account"));
    },
  });

  const createTransactionMutation = useCreateTransaction({
    onSuccess: () => {
      toast.success("Transaction created successfully");
      setShowTransactionForm(false);
      setEditingTransaction(null);
      setTransactionFormData({
        accountId: "",
        type: "credit",
        amount: "",
        description: "",
      });
      refetchTransactions();
      refetchAccounts();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, "Failed to create transaction"));
    },
  });

  const updateTransactionMutation = useUpdateTransaction({
    onSuccess: () => {
      toast.success("Transaction updated successfully");
      setShowTransactionForm(false);
      setEditingTransaction(null);
      setTransactionFormData({
        accountId: "",
        type: "credit",
        amount: "",
        description: "",
      });
      refetchTransactions();
      refetchAccounts();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, "Failed to update transaction"));
    },
  });

  // Update transaction search params when filters change
  useEffect(() => {
    if (hasTxFilters) {
      setTxSearchParams({
        page: 1,
        limit: 50,
        accountId: txAccountFilter || undefined,
        type: txTypeFilter || undefined,
        startDate: txStartDate || undefined,
        endDate: txEndDate || undefined,
        minAmount: txMinAmount ? parseFloat(txMinAmount) : undefined,
        maxAmount: txMaxAmount ? parseFloat(txMaxAmount) : undefined,
      });
    }
  }, [
    txAccountFilter,
    txTypeFilter,
    txStartDate,
    txEndDate,
    txMinAmount,
    txMaxAmount,
    hasTxFilters,
  ]);

  // Search transactions by filters
  const searchTransactions = useCallback(() => {
    // The useEffect above will trigger the query automatically
    // Just ensure we're on page 1
    setTxSearchParams((prev) => ({ ...prev, page: 1 }));
  }, []);

  const clearTxFilters = useCallback(() => {
    setTxAccountFilter("");
    setTxTypeFilter("");
    setTxStartDate("");
    setTxEndDate("");
    setTxMinAmount("");
    setTxMaxAmount("");
    setTxSearchParams({
      page: 1,
      limit: 50,
      accountId: "",
      type: "",
      startDate: "",
      endDate: "",
      minAmount: "",
      maxAmount: "",
    });
  }, []);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Account form handlers
  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountFormData.accountNumber.trim() || !accountFormData.name.trim()) {
      toast.error("Account number and name are required");
      return;
    }

    if (editingAccount) {
      const updateData: any = {
        balance: parseFloat(accountFormData.balance) || 0,
      };

      if (!editingAccount.isCashAccount) {
        updateData.name = accountFormData.name.trim();
      }

      if (editingAccount.isCashAccount) {
        updateData.autoUpdateFromDonations = accountFormData.autoUpdateFromDonations;
      }

      updateAccountMutation.mutate({
        id: editingAccount._id,
        data: updateData,
      });
    } else {
      createAccountMutation.mutate({
        accountNumber: accountFormData.accountNumber.trim(),
        name: accountFormData.name.trim(),
        balance: parseFloat(accountFormData.balance) || 0,
      });
    }
  };

  // Transaction form handlers
  const handleTransactionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !transactionFormData.accountId ||
      !transactionFormData.amount ||
      !transactionFormData.description.trim()
    ) {
      toast.error("Account, amount, and description are required");
      return;
    }

    if (editingTransaction) {
      updateTransactionMutation.mutate({
        id: editingTransaction._id,
        data: {
          type: transactionFormData.type,
          amount: parseFloat(transactionFormData.amount),
          description: transactionFormData.description.trim(),
        },
      });
    } else {
      createTransactionMutation.mutate({
        accountId: transactionFormData.accountId,
        type: transactionFormData.type,
        amount: parseFloat(transactionFormData.amount),
        description: transactionFormData.description.trim(),
      });
    }
  };

  // Edit transaction (open modal pre-filled)
  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setTransactionFormData({
      accountId:
        (transaction as any).accountId &&
        typeof (transaction as any).accountId === "string"
          ? (transaction as any).accountId
          : (transaction as any).accountId?._id || "",
      type: transaction.type,
      amount: String(transaction.amount),
      description: transaction.description || "",
    });
    setShowTransactionForm(true);
  };

  // Edit account
  const handleEditAccount = (account: Account) => {
    setEditingAccount(account);
    setAccountFormData({
      accountNumber: account.accountNumber,
      name: account.name,
      balance: account.balance.toString(),
      autoUpdateFromDonations: account.autoUpdateFromDonations || false,
    });
    setShowAccountForm(true);
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (!deletingAccount) return;
    deleteAccountMutation.mutate(deletingAccount._id);
  };

  // Toggle account status
  const handleToggleStatus = async (account: Account) => {
    try {
      // This would need a mutation hook, for now using direct API call
      const { AccountsAPI } = await import("../../lib/sdk/index");
      await AccountsAPI.toggleAccountStatus(account._id);
      toast.success("Account status updated successfully");
      refetchAccounts();
    } catch (error: any) {
      toast.error(getErrorMessage(error, "Failed to update account status"));
    }
  };

  // Close modals
  const closeAccountForm = () => {
    setShowAccountForm(false);
    setEditingAccount(null);
    setAccountFormData({
      accountNumber: "",
      name: "",
      balance: "",
      autoUpdateFromDonations: false,
    });
  };

  const closeTransactionForm = () => {
    setShowTransactionForm(false);
    setEditingTransaction(null);
    setTransactionFormData({
      accountId: "",
      type: "credit",
      amount: "",
      description: "",
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Accounts Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage financial accounts and transactions
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={() => setShowTransactionForm(true)}
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New Transaction
          </Button>
          {user?.role === "SuperAdmin" && (
            <Button
              onClick={() => setShowAccountForm(true)}
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Add Account
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {canManage && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Wallet className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Balance
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Rs{" "}
                {accounts
                  .reduce((sum: number, acc: any) => sum + acc.balance, 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Active Accounts
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {accounts.filter((a: any) => a.status === "active").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <TrendingDown className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Inactive Accounts
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {accounts.filter((a: any) => a.status === "inactive").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Accounts
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {accounts.length}
              </p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by account number or name..."
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex items-end space-x-2">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Filter className="h-4 w-4" />}
              >
                Apply Filters
              </Button>
              <Button type="button" variant="secondary" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Accounts Table */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Accounts
        </h3>
        {tableLoading ? (
          <div className="text-center py-12">
            <LoadingSpinner />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Loading accounts...
            </p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12">
            <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No accounts found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter
                ? "Try adjusting your search criteria."
                : "Get started by adding your first account."}
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Account No</TH>
                    <TH>Name</TH>
                    <TH>Balance</TH>
                    <TH>Status</TH>
                    {canManage && <TH>Type</TH>}
                    {canManage && <TH>Updated At</TH>}
                    {user?.role === "SuperAdmin" && <TH>Actions</TH>}
                  </TR>
                </THead>
                <TBody>
                  {accounts.map((account: any) => (
                    <TR key={account._id}>
                      <TD className="font-mono">{account.accountNumber}</TD>
                      <TD className="font-medium">{account.name}</TD>
                      <TD className="font-mono font-medium text-green-600 dark:text-green-400">
                        Rs{" "}
                        {safeParseNumber(account.balance, 0).toLocaleString()}
                      </TD>
                      <TD>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            account.status === "active"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                          }`}
                        >
                          {account.status}
                        </span>
                      </TD>
                      {canManage && (
                        <TD>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              account.isCashAccount
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            }`}
                          >
                            {account.isCashAccount ? "Cash" : "Regular"}
                          </span>
                        </TD>
                      )}
                      {canManage && (
                        <TD className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(account.updatedAt || account.updated_at)}
                        </TD>
                      )}
                      {user?.role === "SuperAdmin" && (
                        <TD>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAccount(account)}
                              leftIcon={<Edit className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(account)}
                              disabled={account.isCashAccount}
                              className={
                                account.status === "active"
                                  ? "text-orange-600 hover:text-orange-700"
                                  : "text-green-600 hover:text-green-700"
                              }
                            >
                              {account.status === "active"
                                ? "Deactivate"
                                : "Activate"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeletingAccount(account);
                                setShowDeleteConfirm(true);
                              }}
                              leftIcon={<Trash2 className="h-4 w-4" />}
                              disabled={account.isCashAccount}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {(currentPage - 1) * 10 + 1} to{" "}
                    {Math.min(currentPage * 10, totalAccounts)} of{" "}
                    {totalAccounts} results
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Transactions Filters */}
      <div className="card">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Transactions
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Search by account, amount, date, or type
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
          <div>
            <label className="form-label">Account</label>
            <select
              value={txAccountFilter}
              onChange={(e) => setTxAccountFilter(e.target.value)}
              className="form-select"
            >
              <option value="">All Accounts</option>
              {accounts.map((acc: any) => (
                <option key={acc._id} value={acc._id}>
                  {acc.name} ({acc.accountNumber})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Type</label>
            <select
              value={txTypeFilter}
              onChange={(e) => setTxTypeFilter(e.target.value)}
              className="form-select"
            >
              <option value="">All</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>
          <div>
            <label className="form-label">Start Date</label>
            <Input
              type="date"
              value={txStartDate}
              onChange={(e) => setTxStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">End Date</label>
            <Input
              type="date"
              value={txEndDate}
              onChange={(e) => setTxEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">Min Amount</label>
            <Input
              type="number"
              value={txMinAmount}
              onChange={(e) => setTxMinAmount(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className="form-label">Max Amount</label>
            <Input
              type="number"
              value={txMaxAmount}
              onChange={(e) => setTxMaxAmount(e.target.value)}
              placeholder="100000"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="primary"
            onClick={searchTransactions}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Apply
          </Button>
          <Button variant="secondary" onClick={clearTxFilters}>
            Clear
          </Button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
            Recent Transactions
            <div className="group relative ml-2 flex items-center justify-center">
              <Info className="h-4 w-4 text-gray-400 cursor-help hover:text-primary-500" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs p-2 rounded shadow-lg z-10 text-center">
                For donation transactions, please refer to the Manage Donations page.
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </h3>
        </div>
        {transactions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No recent transactions found
            </p>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Type</TH>
                  <TH>Account</TH>
                  <TH>Amount</TH>
                  <TH>Description</TH>
                  <TH>Created By</TH>
                  <TH>Time</TH>
                  {canManage && <TH>Actions</TH>}
                </TR>
              </THead>
              <TBody>
                {transactions.map((transaction: any) => (
                  <TR key={transaction._id}>
                    <TD>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.type === "credit"
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        }`}
                      >
                        {transaction.type}
                      </span>
                    </TD>
                    <TD>{transaction.accountName}</TD>
                    <TD className="font-mono font-medium text-green-600 dark:text-green-400">
                      Rs{" "}
                      {safeParseNumber(transaction.amount, 0).toLocaleString()}
                    </TD>
                    <TD>{transaction.description}</TD>
                    <TD>{transaction.createdByName}</TD>
                    <TD className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(
                        transaction.createdAt || transaction.created_at,
                      )}
                    </TD>
                    {canManage && (
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTransaction(transaction)}
                            leftIcon={<Edit className="h-4 w-4" />}
                          >
                            Edit
                          </Button>
                        </div>
                      </TD>
                    )}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
      </div>

      {/* Add/Edit Account Form Modal */}
      {showAccountForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editingAccount ? "Edit Account" : "Add New Account"}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeAccountForm}
                leftIcon={<X className="h-4 w-4" />}
              >
                Close
              </Button>
            </div>

            <form onSubmit={handleAccountSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Account Number *</label>
                  <Input
                    type="text"
                    value={accountFormData.accountNumber}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        accountNumber: e.target.value,
                      })
                    }
                    placeholder="Enter account number"
                    required
                    disabled={!!editingAccount}
                  />
                </div>

                <div>
                  <label className="form-label">Account Name *</label>
                  <Input
                    type="text"
                    value={accountFormData.name}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        name: e.target.value,
                      })
                    }
                    placeholder="Enter account name"
                    required
                    disabled={!!editingAccount?.isCashAccount}
                  />
                </div>

                <div>
                  <label className="form-label">Initial Balance</label>
                  <Input
                    type="number"
                    value={accountFormData.balance}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        balance: e.target.value,
                      })
                    }
                    placeholder="Enter initial balance"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              {editingAccount?.isCashAccount && (
                <div className="flex items-center mt-4">
                  <input
                    type="checkbox"
                    id="autoUpdateFromDonations"
                    checked={accountFormData.autoUpdateFromDonations || false}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        autoUpdateFromDonations: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="autoUpdateFromDonations" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Automatically Add Donations to Balance
                  </label>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeAccountForm}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {editingAccount ? "Update" : "Create"} Account
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Transaction Form Modal */}
      {showTransactionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editingTransaction ? "Edit Transaction" : "New Transaction"}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeTransactionForm}
                leftIcon={<X className="h-4 w-4" />}
              >
                Close
              </Button>
            </div>

            <form onSubmit={handleTransactionSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Transaction Type *</label>
                  <select
                    value={transactionFormData.type}
                    onChange={(e) =>
                      setTransactionFormData({
                        ...transactionFormData,
                        type: e.target.value as "credit" | "debit",
                      })
                    }
                    className="form-select"
                    required
                  >
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Account *</label>
                  <select
                    value={transactionFormData.accountId}
                    onChange={(e) =>
                      setTransactionFormData({
                        ...transactionFormData,
                        accountId: e.target.value,
                      })
                    }
                    className="form-select"
                    required
                    disabled={!!editingTransaction}
                  >
                    <option value="">Select account</option>
                    {accounts
                      .filter((acc) => acc.status === "active")
                      .map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.name} ({account.accountNumber}) - Rs{" "}
                          {account.balance.toLocaleString()}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Amount *</label>
                  <Input
                    type="number"
                    value={transactionFormData.amount}
                    onChange={(e) =>
                      setTransactionFormData({
                        ...transactionFormData,
                        amount: e.target.value,
                      })
                    }
                    placeholder="Enter amount"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Description *</label>
                  <Input
                    type="text"
                    value={transactionFormData.description}
                    onChange={(e) =>
                      setTransactionFormData({
                        ...transactionFormData,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter description"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeTransactionForm}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {editingTransaction
                    ? "Update Transaction"
                    : "Create Transaction"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                  <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Delete Account
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Are you sure you want to delete the account "
                  {deletingAccount.name}"? This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-center space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button variant="error" onClick={handleDeleteAccount}>
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
